import React, { useRef, useState } from 'react';
import { FaCamera, FaTimes } from 'react-icons/fa';

interface PhotoUploadProps {
  onPhotosChange: (photos: string[]) => void;
  photos?: string[];
  capture?: 'environment' | 'user';
  maxPhotos?: number;
  label?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onPhotosChange,
  photos = [],
  capture = 'environment',
  maxPhotos = 5,
  label = 'Photos',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    setUploading(true);
    const newPhotos = [...photos];
    for (let i = 0; i < files.length && newPhotos.length < maxPhotos; i++) {
      const file = files[i];
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      newPhotos.push(dataUrl);
    }
    onPhotosChange(newPhotos);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removePhoto = (index: number) => {
    const updated = photos.filter((_, i) => i !== index);
    onPhotosChange(updated);
  };

  return (
    <div>
      <label className="block text-slate-700 font-semibold mb-2 text-sm">{label}</label>
      <div className="flex flex-wrap gap-3">
        {photos.map((photo, idx) => (
          <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200">
            <img src={photo} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(idx)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
            >
              <FaTimes />
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-slate-500 hover:text-slate-600 transition-all"
          >
            {uploading ? (
              <span className="text-xs">Uploading...</span>
            ) : (
              <>
                <FaCamera size={20} />
                <span className="text-xs mt-1">Add</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture={capture}
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};

export default PhotoUpload;
